<template>
  <PeopleAndGroupsTemplate
    tab="people"
    :selectedLength="selected.length"
    :clearSelected="clearSelected"
    @update-search="handleSearchValueChange"
  >
    <template #selected-count>
      {{ selected.length }}
      {{ selected.length === 1 ? "Person" : "People" }}
      Selected
    </template>
    <template #actions>
      <v-tooltip v-if="permissions.includes('users:create:user')" top open-delay="200">
        <template v-slot:activator="{ on, attrs }">
          <Button
            v-on="on"
            v-bind="attrs"
            :disabled="!selectedUninvitedUsers.length"
            text
            small
            @click="sendInviteEmail"
          >
            Send {{ applicationName }} invite
          </Button>
        </template>
        <span
          >If any email/password users have already logged in, <br />they will get a password reset
          email instead of an invite.</span
        >
      </v-tooltip>
      <Button
        v-if="permissions.includes('groups:create:group')"
        :disabled="!selected.length"
        text
        small
        @click="showExistingGroupDialog = true"
      >
        Add to existing group
      </Button>
      <Button
        v-if="permissions.includes('groups:create:group')"
        :disabled="!selected.length"
        text
        small
        @click="showNewGroupDialog = true"
      >
        Create a new group
      </Button>
      <Button
        v-if="permissions.includes('vue:read:communicationwizard') && !isMobile"
        :disabled="!selected.length"
        text
        small
        @click="loadCommunicationWizard"
      >
        Send message
      </Button>
      <Button
        v-if="permissions.includes('users:delete:user')"
        :disabled="!selected.length"
        text
        small
        @click="confirmDeleteMultiple = true"
      >
        Delete selected people
      </Button>
      <div class="full-width divider mt-2 mb-3"></div>
      <Button
        v-if="permissions.includes('users:delete:user')"
        text
        small
        @click="showBulkDeleteDialog = true"
      >
        Bulk Delete via CSV
      </Button>
      <Button
        v-if="permissions.includes('users:create:export')"
        text
        small
        @click="showUserExportDialog = true"
      >
        Export Users
      </Button>
      <Button
        v-if="hasPermission('admin:create:user-merge')"
        text
        small
        @click="$router.push({ name: 'admin-merge-users' })"
      >
        Merge Users
      </Button>
    </template>
    <template #search-row-actions>
      <v-menu v-model="userFilterMenu" offset-y :close-on-content-click="false">
        <template v-slot:activator="{ on, attrs }">
          <Button v-if="permissions.includes('users:create:user')" text v-bind="attrs" v-on="on">
            Filter
          </Button>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-action>
              <v-checkbox
                v-model="userFilterValue"
                @change="updateFilter"
                value="uninvited"
              ></v-checkbox>
            </v-list-item-action>

            <v-list-item-content class="text-left">
              <v-list-item-title>Show uninvited people</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <Button v-if="permissions.includes('users:create:user')" @click="loadUserForm"
        >Add People</Button
      >
    </template>
    <template #data-table>
      <UserDataTable
        :selected="selected"
        @update:selected="selected = $event"
        :columns="peopleColumns"
        :users="visibleUsers"
        :isPointsOnlyClient="isPointsOnlyClient"
        :options="table.options"
        @update:options="table.options = $event"
        :total="table.total"
        :itemsPerPage="table.itemsPerPage"
        :footerProps="table.footerProps"
        :loading="table.loading"
        :search="table.search"
        :clickableRows="canOpenUserDrawer"
        @row-click="loadEditUserForm"
        @checkbox-click="(item) => addUserToSelected(item, false)"
        @wallet-click="handleWalletClick"
      />
    </template>

    <template #overlay>
      <div>
        <v-navigation-drawer v-model="showUserDialog" temporary fixed right width="500">
          <UserCreatorWidget
            v-if="showUserDialog"
            @close="closeUserNavDrawer"
            @get-users="getUsersV2(true)"
            :roles="data.roles"
            :clientId="clientId"
            :isPointsOnlyClient="isPointsOnlyClient"
            :editingUser="editingUserForm"
            :editUserObject="editingUserForm ? newUser : null"
            :viewOnly="readonlyUserDrawer"
            :canSendInvite="canOpenUserDrawer"
            source="PEOPLE"
          ></UserCreatorWidget>
        </v-navigation-drawer>

        <v-navigation-drawer v-model="showBulkDeleteDialog" temporary fixed right width="500">
          <UserDeletionWidget
            v-if="showBulkDeleteDialog"
            @close="showBulkDeleteDialog = false"
            @get-users="getUsersV2(true)"
            @reset-selected="selected = []"
            :roles="data.roles"
            :clientId="clientId"
            :editingUser="editingUserForm"
            :editUserObject="newUser"
            source="PEOPLE"
          ></UserDeletionWidget>
        </v-navigation-drawer>
        <v-navigation-drawer v-model="showUserExportDialog" temporary fixed right width="500">
          <CSVExport
            v-if="showUserExportDialog"
            @close="showUserExportDialog = false"
            table="UserClient"
          ></CSVExport>
        </v-navigation-drawer>
        <DeleteMultipleUsersDialog
          :show="confirmDeleteMultiple"
          @update:show="confirmDeleteMultiple = $event"
          :selectedUsers="selected"
          :currentUserId="userProfile.userId"
          @delete="deleteUsers"
        />

        <CreateNewGroupDialog
          :show="showNewGroupDialog"
          @update:show="showNewGroupDialog = $event"
          :selectedUsers="pruneSelectedForGroup"
          :loading="newGroupForm.loadingGroupTable"
          @create="handleCreateGroup"
          @cancel="resetGroupForm"
        />

        <!-- Dialog used to add users to existing group -->
        <ExistingGroupDialog
          :show="showExistingGroupDialog"
          @update:show="showExistingGroupDialog = $event"
          :groups="listGroups"
          :groupsLoading="listGroupSelect.loading"
          :groupsTotal="listGroupSelect.total"
          :selectedUsers="pruneSelectedForGroup"
          :tableLoading="newGroupForm.loadingGroupTable"
          @add="handleAddToExistingGroup"
          @cancel="resetGroupForm"
          @load-more="getGroupsV2(listGroupSelect.page + 1)"
        />
        <!-- Balance dialog -->
        <CSMWalletManager
          v-if="balances.display"
          :key="balances.userId"
          :userId="balances.userId"
          :clientId="balances.clientId"
          @close="balances.display = false"
        />

        <!-- Dialog used for displaying loading message. -->
        <LoadingDialog
          :showDialog="showLoadingDialog"
          :header="loadingHeaderMessage"
          :line1="loadingMessageLine1"
          :line2="loadingMessageLine2"
        ></LoadingDialog>
      </div>
    </template>
  </PeopleAndGroupsTemplate>
</template>

<script>
import UserService from "@/services/UserService.js";
import OnboardingService from "@/services/OnboardingService.js";
import GroupService from "@/services/GroupService.js";

import LoadingDialog from "@/components/loadingDialog.vue";
import UserCreatorWidget from "@/components/people/UserCreatorWidgetV2.vue";
import UserDeletionWidget from "@/components/people/UserDeletionWidget.vue";
import CSVExport from "@/components/people/CSVExport.vue";
import CSMWalletManager from "@/components/people/CSMWalletManager.vue";
import Button from "@/components/generic/Button.vue";
import CreateNewGroupDialog from "@/components/people/CreateNewGroupDialog.vue";
import DeleteMultipleUsersDialog from "@/components/people/DeleteMultipleUsersDialog.vue";
import UserDataTable from "@/components/people/UserDataTable.vue";
import ExistingGroupDialog from "@/components/people/ExistingGroupDialog.vue";
import PeopleAndGroupsTemplate from "@/components/people-and-groups/PeopleAndGroupsTemplate.vue";

import permissionsMixin from "@/mixins/permissionsMixin";
import isOne10PointsEnabled from "@/mixins/one10Points/isOne10PointsEnabled";
import isPaymentCardEnabled from "@/mixins/one10Points/isPaymentCardEnabled";
import whitelabelMixin from "@/mixins/whitelabelMixin";
import { countries } from "@/shared_data/data.js";
import { mapState, mapGetters } from "vuex";
import moment from "moment";

const createEmptyUser = () => {
  return {
    clientId: null,
    userId: null,
    firstName: null,
    lastName: null,
    userIdAtClient: null,
    profilePicUrl: null,
    businessEmail: null,
    emailDomain: null,
    businessPhone: null,
    personalEmail: null,
    personalPhone: null,
    managerEmail: null,
    status: "Active",
    displayName: null,
    jobTitle: null,
    countryCode: null,

    birthday: null,
    roleStartDate: null,
    jobStartDate: null,
    businessAddress: null,
    businessCity: null,
    businessState: null,
    businessPostalCode: null,
    businessCountry: null,
    otherBusinessCountry: null,
    homeAddress: null,
    homeCity: null,
    homeState: null,
    homePostalCode: null,
    homeCountry: null,
    otherHomeCountry: null,
    organization: null,
    token1: null,
    token2: null,
    token3: null,

    taxpayerType: null,
    fullLegalName: null,
    taxBusinessName: null,
    taxTradeName: null,
    taxClassification: null,
    taxAddress: null,
    taxAddress2: null,
    taxCity: null,
    taxState: null,
    taxPostalCode: null,
    taxCountry: null,

    roles: [],
    statusList: ["Active", "Inactive"],
  };
};

export default {
  name: "People",
  title: "People",
  mixins: [permissionsMixin, isOne10PointsEnabled, isPaymentCardEnabled, whitelabelMixin],
  components: {
    LoadingDialog,
    UserCreatorWidget,
    UserDeletionWidget,
    CSVExport,
    CSMWalletManager,
    Button,
    CreateNewGroupDialog,
    DeleteMultipleUsersDialog,
    UserDataTable,
    ExistingGroupDialog,
    PeopleAndGroupsTemplate,
  },
  props: {
    showNewUserDialogOnLoad: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      anim: null,
      animationSpeed: 1,
      showLoadingDialog: false,
      loadingHeaderMessage: null,
      loadingMessageLine1: null,
      loadingMessageLine2: null,
      loadingUserTable: true,

      selected: [],
      peopleTableSearch: null,
      userFilterValue: null,
      userFilterMenu: false,
      allUsers: [],
      users: [],
      allGroups: [],
      groups: [],
      listGroups: [],
      data: {
        roles: [],
      },
      columns: [
        {
          text: " ",
          align: "center",
          sortable: false,
          value: "profilePicUrl",
          width: "20px",
        },
        { text: "Name/Email or Phone", value: "tableDisplayName", width: "40%" },
        { text: "Organization / Job Title", value: "organization", width: "40%" },
        // { text: "Job Title", value: "jobTitle", width: "30%" },
        {
          text: "External",
          value: "externalUser",
          width: "10%",
          permission: "users:read:externalattribute",
          sortable: false,
        },
        {
          text: "Balance",
          value: "balance",
          width: "10%",
          permission: "users:read:balance-admin",
          sortable: false,
        },
        {
          text: "Wallet",
          value: "wallet",
          width: "10%",
          permission: "admin:read:ledger-balances-internal",
          sortable: false,
        },
      ],
      showUserDialog: false,

      confirmDeleteMultiple: false,
      showNewGroupDialog: false,
      showBulkDeleteDialog: false,
      showUserExportDialog: false,

      editingUserForm: false,
      newUser: createEmptyUser(),
      newGroupForm: {
        valid: false,
        loadingGroupTable: false,
        groupColumns: [
          {
            text: "Name",
            align: "start",
            value: "displayName",
            width: "20%",
          },
          { text: "Email / Phone", value: "businessEmail", width: "20%" },
        ],
      },
      newGroup: {
        name: null,
      },
      showExistingGroupDialog: false,
      existingGroupForm: {
        valid: false,
      },
      existingGroup: {
        groupId: null,
      },

      table: {
        itemsPerPage: 10,
        page: 1,
        // Used if the user ends up mashing the table page button, then we're waiting on multiple responses so we know which page to actually store the data for
        awaitingPage: 1,
        search: null,
        loading: false,
        users: [],
        total: 0,
        selected: [],
        options: {},
        footerProps: {
          "items-per-page-options": [10, 25, 50, 100],
        },
        latestSort: null,
      },
      listGroupSelect: {
        loading: false,
        page: 0,
        total: 0,
        search: null,
        debounceSearch: null,
      },
      balances: {
        display: false,
        userId: null,
        clientId: null,
      },
    };
  },
  created() {
    if (this.$auth) {
      // this.clientId = this.userProfile.clientId;
      this.getUsersV2(true);
      // this.getGroups();
      this.getGroupsV2(0);
      if (this.permissions.includes("users:read:role")) this.getRoles();
      if (this.showNewUserDialogOnLoad) this.loadUserForm();
    }
  },
  mounted() {
    //Move the fresh works help widget
    var freshworks = document.querySelector("#launcher-frame");
    if (freshworks) {
      freshworks.style.right = "-55px";
      freshworks.style["max-width"] = freshworks.style["min-width"] = "90px";
    }
  },
  unmounted() {
    var freshworks = document.querySelector("#launcher-frame");
    if (freshworks) {
      freshworks.style.right = "22px";
    }
  },
  methods: {
    async getUsersV2(reset = false) {
      try {
        this.table.loading = true;
        if (reset) {
          this.table.options.page = 1;
        }

        // Used if the user ends up mashing the table page button, then we're waiting on multiple responses so we know which page to actually store the data for
        let options = this.table.options;
        let page = this.table.options.page;
        let filterValue = this.userFilterValue;
        // if first page, fetch double
        let limit = options.itemsPerPage; //page == 1 ? options.itemsPerPage * 2 : options.itemsPerPage;
        let offset = reset ? 0 : (page - 1) * limit;
        if (this.table.options.sortBy && options.sortBy.length > 0) {
          let column = options.sortBy;
          let type =
            options.sortDesc && options.sortDesc.length > 0 && options.sortDesc[0] === true
              ? "DESC"
              : "ASC";

          // If displayname then we really check display name, first/last name
          if (column == "tableDisplayName") sort = `User.firstName ${type}, User.lastName ${type}`;
          else sort = column + " " + type;
        } else var sort = `User.firstName ASC, User.lastName ASC`;
        sort += `, createDate ASC`;
        console.log("Sort ", sort);
        this.table.latestSort = sort;
        let params = {
          limit,
          offset,
          sort,
          clientId: this.clientId,
          screen: "people",
          search: this.table.search,
          uninvited: this.userFilterValue == "uninvited" ? 1 : 0,
          // Permissions will be checked in the API before actually returning this
          balance: 1,
        };
        let response = await UserService.getUsersV2(params);

        // we have to compare states to see if we ignore results (only if them spam a filter)
        if (
          this.userFilterValue != filterValue ||
          sort != this.table.latestSort ||
          page != this.table.options.page ||
          this.clientId != params.clientId ||
          this.table.search != params.search ||
          limit != this.table.options.itemsPerPage
        ) {
          console.log("Ignoring response", {
            filter: this.userFilterValue != filterValue,
            sort: sort != this.table.latestSort,
            page: page != this.table.options.page,
            clientId: this.clientId != params.clientId,
            search: this.table.search != params.search,
            limit: limit != this.table.options.itemsPerPage,
          });
          return;
        }

        console.log("User response ", response);
        let rows = response.result.rows;

        //Prune the returned users for the data we need for the table
        rows = rows.map(function (user) {
          user.tableDisplayName = user.firstName + " " + user.lastName;
          user.id = user.userId;
          user.name = user.displayName || user.firstName + " " + user.lastName;
          return user;
        });

        this.table.total = response.result.count;
        this.table.users = rows;
        this.table.loading = false;
      } catch (err) {
        console.log("Error getting users ", err);
        this.table.loading = false;
      }
    },
    getRoles() {
      UserService.getRoles()
        .then((response) => {
          this.data.roles = response;
        })
        .catch((error) => {
          console.log("Error getting roles!" + error);
        });
    },
    async getGroupsV2(page) {
      try {
        this.listGroupSelect.loading = true;
        // if (reset) {
        //   this.listGroupSelect.page = 1;
        // }
        this.listGroupSelect.page = page;
        let search = this.listGroupSelect.search;

        let filter = `status != 'Deleted' && clientId=${this.clientId} && groupType = 'List'`;
        if (search) {
          // If search, we need to search for every field that's in the table
          filter += ` && (groupDisplayName.like('%${search}%') || groupName.like('%${search}%'))`;
        }
        // If first page, fetch double
        // let limit = page == 1 ? options.itemsPerPage * 2 : options.itemsPerPage;
        let limit = 20;
        let offset = page * limit;
        let sort = "groupId DESC";
        console.log({
          page: page,
          limit: limit,
          sort: sort,
          filter: filter,
          offset: offset,
        });
        let response = await GroupService.getGroupsV2({
          filter: filter,
          limit: limit,
          offset: offset,
          sort: sort,
          expand: "GroupAssignment.User",
        });
        console.log("Current page ", page);
        console.log("Group response ", response);
        let rows = response.result.rows;
        this.listGroupSelect.total = response.result.count;
        if (page == 0) this.listGroups = rows;
        else this.listGroups = this.listGroups.concat(rows);

        // if (reset) this.table.selected = [];

        this.listGroupSelect.loading = false;
      } catch (error) {
        console.log("Error getting list groups", error);
        this.listGroupSelect.loading = false;
      }
    },
    loadUserForm() {
      this.newUser = createEmptyUser();
      this.editingUserForm = false;
      this.editingUserInformManaged = false;
      this.showUserDialog = true;
    },
    handleCreateGroup(groupName) {
      this.newGroup.name = groupName;
      this.newGroupForm.valid = true;
      this.upsertListGroup(true);
    },
    handleAddToExistingGroup(groupId) {
      this.existingGroup.groupId = groupId;
      this.existingGroupForm.valid = true;
      this.upsertListGroup(false);
    },
    async upsertListGroup(newGroup = false) {
      // If they hit enter with an invalid form, then we ignore for now
      if (!this.newGroupForm.valid && newGroup === true) return;
      //This function grabs anyone currently assigned to the chosen group and appends anyone selected to it
      const createGroup = !!this.showNewGroupDialog;
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = `${createGroup ? "Creating" : "Updating"} the ${
        createGroup ? "new" : "chosen"
      } group`;
      this.loadingMessageLine1 = "You'll just need to wait a sec, this shouldn't take long!";
      this.loadingMessageLine2 = "";

      try {
        const existingGroup = this.listGroups.find((x) => x.groupId === this.existingGroup.groupId);
        if (!existingGroup && !createGroup) throw "No group found";
        const existingUsers =
          !createGroup && existingGroup.GroupAssignments
            ? existingGroup.GroupAssignments.map((x) => x.userId)
            : [];

        let group = {
          clientId: this.clientId,
          groupId: createGroup ? undefined : existingGroup.groupId,
          groupName: createGroup ? this.newGroup.name : existingGroup.groupDisplayName,
          groupDisplayName: createGroup ? this.newGroup.name : existingGroup.groupDisplayName,
          source: "PEOPLE",
          userIds: [existingUsers, this.selected.map((x) => x.userId)].flat(),
        };

        console.log("Group we're going to create/update ", group);

        let res = await GroupService.createGroupV2(group, "list");
        console.log("Got list group response ", res);
        this.resetGroupForm();
        this.getGroupsV2(0);
        this.selected = [];
      } catch (err) {
        console.log("Error creating/updating list group ", err);
      } finally {
        this.showLoadingDialog = false;
      }
    },
    resetGroupForm() {
      if (this.$refs.newGroupForm) this.$refs.newGroupForm.reset();
      if (this.$refs.existingGroupForm) this.$refs.existingGroupForm.reset();
      this.showNewGroupDialog = false;
      this.showExistingGroupDialog = false;
    },
    handleWalletClick(item) {
      this.balances.display = true;
      this.balances.userId = item.userId;
      this.balances.clientId = item.clientId;
    },
    async loadEditUserForm(user) {
      // Wipe any old values before loading in the user to edit
      this.newUser = createEmptyUser();
      var userId = user.userId;
      // var user = this.table.users.find(x => x.userId === userId);
      this.newUser.originalBusinessEmail = user.businessEmail;
      // var index = user.businessEmail.indexOf("@");
      // var emailPrefix = user.businessEmail.substring(0, index);
      // var emailDomain = user.businessEmail.substring(index + 1);
      this.newUser.clientId = user.clientId;
      this.newUser.userId = userId;
      this.newUser.firstName = user.firstName;
      this.newUser.lastName = user.lastName;
      this.newUser.userIdAtClient = user.userIdAtClient;
      this.newUser.businessEmail = user.businessEmail;
      // this.newUser.emailDomain = emailDomain;
      this.newUser.businessPhone = user.businessPhone;
      this.newUser.personalEmail = user.personalEmail;
      this.newUser.personalPhone = user.personalPhone;
      this.newUser.managerEmail = user.managerEmail;
      this.newUser.status = user.status;
      this.newUser.displayName = user.displayName;
      this.newUser.jobTitle = user.jobTitle;
      this.newUser.countryCode = user.countryCode;
      this.newUser.startDate = user.startDate;
      this.newUser.profilePicUrl = user.profilePicUrl;
      // this.newUser.lastActive = user.lastActive;
      this.newUser.firstLogin = user.firstLogin;
      this.newUser.birthday = user.birthday;
      this.newUser.roleStartDate = user.roleStartDate;
      this.newUser.jobStartDate = user.jobStartDate;
      this.newUser.businessAddress = user.businessAddress;
      this.newUser.businessCity = user.businessCity;
      this.newUser.businessState = user.businessState;
      this.newUser.businessPostalCode = user.businessPostalCode;
      console.log(user.businessCountry);
      this.newUser.businessCountry = null;
      if (user.businessCountry === null) {
        this.newUser.businessCountry = null;
        this.newUser.otherBusinessCountry = null;
      } else if (countries.some((c) => c.code === user.businessCountry && c.code !== "00")) {
        this.newUser.businessCountry = user.businessCountry;
      } else {
        this.newUser.businessCountry = "00";
        this.newUser.otherBusinessCountry = user.businessCountry;
      }
      this.newUser.homeAddress = user.homeAddress;
      this.newUser.homeCity = user.homeCity;
      this.newUser.homeState = user.homeState;
      this.newUser.homePostalCode = user.homePostalCode;
      console.log(user.homeCountry);
      if (user.homeCountry === null) {
        this.newUser.homeCountry = null;
        this.newUser.otherHomeCountry = null;
      } else if (countries.some((c) => c.code === user.homeCountry && c.code !== "00")) {
        this.newUser.homeCountry = user.homeCountry;
      } else {
        this.newUser.homeCountry = "00";
        this.newUser.otherHomeCountry = user.homeCountry;
      }
      this.newUser.organization = user.organization;
      this.newUser.annualPaymentCap = user.annualPaymentCap;
      this.newUser.externalUser = user.externalUser;
      this.newUser.primaryClientId = user.primaryClientId;

      this.newUser.token1 = user.token1;
      this.newUser.token2 = user.token2;
      this.newUser.token3 = user.token3;

      this.newUser.taxpayerType = user.taxpayerType;
      this.newUser.fullLegalName = user.fullLegalName;
      this.newUser.taxBusinessName = user.taxBusinessName;
      this.newUser.taxTradeName = user.taxTradeName;
      this.newUser.taxClassification = user.taxClassification;
      this.newUser.taxAddress = user.taxAddress;
      this.newUser.taxAddress2 = user.taxAddress2;
      this.newUser.taxCity = user.taxCity;
      this.newUser.taxState = user.taxState;
      this.newUser.taxPostalCode = user.taxPostalCode;
      this.newUser.taxCountry = user.taxCountry;

      this.newUser.passwordEnabled = user.passwordEnabled;
      this.newUser.emailPasswordlessEnabled = user.emailPasswordlessEnabled;
      this.newUser.smsPasswordlessEnabled = user.smsPasswordlessEnabled;

      this.newUser.onboardingEmailSent = user.onboardingEmailSent;
      this.newUser.onboardingTextSent = user.onboardingTextSent;
      this.newUser.informManaged = user.informManaged;
      if (user.balance !== undefined) this.newUser.balance = user.balance;
      if (user.pointBalance !== undefined) this.newUser.pointBalance = user.pointBalance;
      if (!user.onboardingEmailSent) {
        // Converts UTC back to local time to prefil into the timestamp selector
        if (!user.scheduledOnboardingDate)
          this.newUser.scheduledOnboardingDate = user.scheduledOnboardingDate;
        else {
          console.log(user.scheduledOnboardingDate);
          this.newUser.scheduledOnboardingDate = moment(
            moment.utc(user.scheduledOnboardingDate).toDate()
          )
            .local()
            .format("YYYY-MM-DDTHH:mm");
        }
      }

      // var userRoles = await UserService.getRolesByUser(user.userId).catch(
      //   () => {
      //     return [];
      //   }
      // );
      // // Remove any that start with 9
      // this.newUser.roles = userRoles
      // .map(x => x.id);

      this.editingUserForm = true;
      this.showUserDialog = true;
    },

    deleteUsers() {
      //We have one endpoint to delete a single user, so we create a loop to call that endpoint for every user.
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Deleting selected people";
      this.loadingMessageLine1 = "Relax while we do the hard part!";
      this.loadingMessageLine2 = "Feel free to close this page. We'll email you when we're done";
      //we call the loop because the button is disabled if no one is selected, so we assume the array is not empty
      //The context changes within the function so we have to define 'this'
      var users = this.selected.map((x) => x.userId);

      UserService.deleteUsers(users, { clientId: this.clientId })
        .then((response) => {
          console.log(response);
          if (response && response.data && response.data.success) console.log("Users deleted");
          else console.log("Error deleting user");

          this.showLoadingDialog = false;
          this.getUsersV2(true);
          this.confirmDeleteMultiple = false;
          this.selected = [];
        })
        .catch((error) => {
          console.log(error);
          this.showLoadingDialog = false;
          this.confirmDeleteMultiple = false;
        });
    },
    loadCommunicationWizard() {
      this.$router.push({
        name: "communicationwizard",
        query: {
          previousRoute: this.$route.name,
        },
        params: {
          userId: this.selected.map((x) => x.userId || x),
        },
      });
    },
    closeUserNavDrawer(clear) {
      this.editingUserForm = false;
      this.newUser = createEmptyUser();
      if (clear === true) this.selected = [];
      this.showUserDialog = false;
    },

    refreshClientId() {
      this.selected = [];
      this.table.users = [];
      this.getUsersV2(true);
      this.getGroupsV2(0);
    },
    updateFilter() {
      // this.selected = [];
      this.getUsersV2(true);
    },
    clearSelected() {
      this.selected = [];
    },
    sendInviteEmail() {
      this.loadingHeaderMessage = "Sending invitations";
      this.loadingMessageLine1 = "Please wait";
      this.loadingMessageLine2 = "This should be pretty quick!";
      this.showLoadingDialog = true;
      var users = this.selectedUninvitedUsers.map((x) => x.userId);
      OnboardingService.sendWhistleInviteEmails(users, this.clientId)
        .then((response) => {
          console.log("Response from sending onboarding invite emails: ", response);
          this.showLoadingDialog = false;
          this.selected = [];
          this.getUsersV2(true);
        })
        .catch((error) => {
          console.log("Error!", error);
          this.showLoadingDialog = false;
        });
    },
    addUserToSelected(user) {
      // If they're adding the same group twice, we want to replace
      // This is because when loadBuilder is called, we temporarily put ProgramGroups as the audience
      // But they don't have a name. So once getGroups returns, we replace with groups with names
      console.log("Add user to selected");
      let existingUserIndex = this.selected.findIndex((x) => x.userId == user.userId);
      if (existingUserIndex != -1) this.selected.splice(existingUserIndex, 1);
      else this.selected.push(user);

      // Then we find the group in the main array and mark as display = false;
      // var groupIndex = this.data.groups.findIndex(
      //   x => x.groupId == group.groupId
      // );
      // if (groupIndex !== -1) this.data.groups[groupIndex].display = false;
    },
    handleSearchValueChange(val) {
      this.table.search = val;
      this.getUsersV2(true);
    },
    handleGroupPageClick() {
      this.$router.push("/people/groups");
    },
  },
  computed: {
    ...mapState([
      "userProfile",
      "clients",
      "postItArray",
      "permissions",
      "roles",
      "globalClientId",
    ]),
    ...mapGetters(["hasPermissionToViewClients"]),
    canOpenUserDrawer() {
      return this.permissions?.includes("users:create:user") || this.hasPermissionToViewClients();
    },
    readonlyUserDrawer() {
      return this.canOpenUserDrawer && !this.permissions?.includes("users:create:user");
    },
    clientId: {
      get: function () {
        return this.globalClientId;
      },
      set: function (newVal) {
        this.$store.dispatch("setClientId", newVal);
      },
    },
    pruneSelectedForGroup() {
      var usersPruned = this.selected.map(function (row) {
        return {
          displayName: row.tableDisplayName,
          businessEmail: row.businessEmail,
          businessPhone: row.businessPhone,
        };
      });
      return usersPruned;
    },
    visibleUsers() {
      return this.table.users;
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    isPointsOnlyClient() {
      return this.isOne10PointsEnabled(this.clientId) && !this.isPaymentCardEnabled(this.clientId);
    },
    peopleColumns() {
      // If on mobile then we declutter and just show the name/email
      return this.$vuetify.breakpoint.xs
        ? [{ ...this.columns[1] }]
        : this.columns
            .filter((x) => !(this.isPointsOnlyClient && x.value === "wallet"))
            .filter(
              (x) =>
                (!x.permission && !x.roles) ||
                this.permissions.includes(x.permission) ||
                (x.roles && !!x.roles?.find((r1) => this.roles?.some((r2) => r2 === r1)))
            );
    },
    selectedUninvitedUsers() {
      return this.selected.filter(
        (x) =>
          // (!x.onboardingEmailSent && x.passwordEnabled && x.businessEmail) ||
          (x.passwordEnabled && x.businessEmail) ||
          (!x.passwordEnabled &&
            x.smsPasswordlessEnabled &&
            // !x.onboardingTextSent &&
            x.businessPhone &&
            !x.externalUser)
      );
    },
  },
  watch: {
    clientId: function () {
      this.refreshClientId();
    },
    // This watches any options configured for the data table so we know if the user interacts and changes the sorting
    "table.options": {
      handler(newVal, oldVal) {
        // Used to indicate if we wipe the table and start with an empty array, versus pushing more data on
        var reset = false;
        var apiCall = true;
        // If none of these exist then we're probably loading the page for the first time so we reset
        if (!oldVal.itemsPerPage || !oldVal.page || !oldVal.sortBy || !oldVal.sortDesc)
          reset = true;
        else {
          // If any values changed from their old to new states, then we reset because they're changing a sort
          if (
            newVal.itemsPerPage !== oldVal.itemsPerPage ||
            (newVal.sortBy && oldVal.sortBy && newVal.sortBy[0] !== oldVal.sortBy[0]) ||
            (newVal.sortDesc && oldVal.sortDesc && newVal.sortDesc[0] !== oldVal.sortDesc[0])
          )
            reset = true;
        }

        if (apiCall) this.getUsersV2(reset);
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Adds border around profile picture */
.profile-picture {
  border: 2px solid;
  border-color: var(--v-grey-base);
}

/* Locks size of placeholder profile picture */
.icon-image {
  height: 35px;
  width: 35px;
}

/* Divider because v-divider doesn't appear when flex-direction is column */
.divider {
  border: 0.5px solid lightgray;
}
</style>
